import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import RouteLoadingGate from './RouteLoadingGate';
import { useRegistration } from '../context/RegistrationContext';
import { useMsmeProfileQuery } from '../hooks/queries/useMsmeWorkspaceQueries';
import { getCurrentUserRole, isAdminPortalUser, isSupportPortalUser, isExpertPortalUser } from '../utils/authRole';
import { isPartnerPortalSession } from '../utils/partnerPortalSession';

interface RequireMSMEProps {
    children: React.ReactNode;
}

const httpStatus = (error: unknown): number | undefined => {
    const candidate = error as { response?: { status?: number }; status?: number };
    return candidate?.response?.status ?? candidate?.status;
};

/**
 * Wrapper component that checks if the current user has MSME data.
 * If user is MSME role but has no business data, redirects to registration.
 * A fresh React Query profile is rendered immediately. The gate stays only
 * while that profile is actually missing.
 */
const RequireMSME: React.FC<RequireMSMEProps> = ({ children }) => {
    const navigate = useNavigate();
    const { logout } = useRegistration();
    const enterpriseRedirect = getCurrentUserRole() === 'enterprise';
    const portalRedirect = isAdminPortalUser() || isSupportPortalUser() || isExpertPortalUser() || isPartnerPortalSession() || enterpriseRedirect;
    const profileQuery = useMsmeProfileQuery(!portalRedirect);
    const [verificationFailed, setVerificationFailed] = useState(false);
    const profile = profileQuery.data?.data;

    useEffect(() => {
        if (isAdminPortalUser()) {
            navigate('/admin/dashboard', { replace: true });
            return;
        }
        if (isSupportPortalUser()) {
            navigate('/support/dashboard', { replace: true });
            return;
        }
        if (isExpertPortalUser()) {
            navigate('/expert/dashboard', { replace: true });
            return;
        }
        if (isPartnerPortalSession()) {
            navigate('/partner/dashboard', { replace: true });
            return;
        }
        if (getCurrentUserRole() === 'enterprise') {
            navigate('/enterprise/dashboard', { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        if (portalRedirect || profile || profileQuery.isLoading || profileQuery.isFetching) {
            return;
        }
        if (profileQuery.isError) {
            const status = httpStatus(profileQuery.error);
            const message = String((profileQuery.error as Error)?.message || '').toLowerCase();
            if (message.includes('not found') || status === 404) {
                navigate('/msme-registration');
                return;
            }
            if (status === 401 || status === 403) {
                logout({ redirectTo: '/login' });
                return;
            }
            setVerificationFailed(true);
            return;
        }
        if (profileQuery.isSuccess) {
            navigate('/msme-registration');
        }
    }, [
        portalRedirect,
        profileQuery.isLoading,
        profileQuery.isFetching,
        profileQuery.isError,
        profileQuery.isSuccess,
        profileQuery.error,
        profile,
        navigate,
        logout,
    ]);

    if (portalRedirect) {
        return null;
    }

    if (profile) {
        return <>{children}</>;
    }

    if (profileQuery.isLoading || profileQuery.isFetching) {
        return (
            <RouteLoadingGate message="Checking your company profile…" minHeight="400px" />
        );
    }

    const failureStatus = httpStatus(profileQuery.error);
    const failureMessage = String((profileQuery.error as Error)?.message || '').toLowerCase();
    if (
        failureStatus === 401
        || failureStatus === 403
        || failureStatus === 404
        || failureMessage.includes('not found')
    ) {
        return null;
    }

    if (verificationFailed || profileQuery.isError) {
        return (
            <Box sx={{ p: 4, maxWidth: 480, mx: 'auto' }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    We could not verify your company profile. Please try again in a moment.
                </Alert>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setVerificationFailed(false);
                            void profileQuery.refetch();
                        }}
                    >
                        Retry
                    </Button>
                    <Button variant="text" onClick={() => navigate('/login')}>
                        Back to sign in
                    </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Reviewer accounts should use the admin &amp; reviewer portal instead.
                </Typography>
            </Box>
        );
    }

    return null;
};

export default RequireMSME;
